'use client';

/**
 * Heroes from a photo, made AT UPLOAD (pipeline v2 «Герои заказа»): the photo
 * and the guardian consent go up, the character record is created with its id
 * and its 3D identity starts drawing at once. The wizard keeps the id (and the
 * photo key, for «Перерисовать»); the order carries characterIds only.
 *
 * Shared by HeroesStep and the from-text cast picker (StoryInputStep).
 */
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Camera, CheckCircle2, Loader2, RefreshCw, X } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { PhotoConsentCheckbox, characterKeys } from '@/contexts/characters';
import { PHOTO_CONSENT_VERSION } from '@/contexts/characters/photoConsent';
import { fileToBase64 } from '@/contexts/characters/utils/fileToBase64';
import {
  createPhotoHero, getPhotoHeroStates, redrawPhotoHero, renamePhotoHero,
} from '@/contexts/characters/actions/photoHero';
import type { ContentParameters, PhotoHeroDraft } from './ParametersStep';
import { whileWaiting } from '@/shared/hooks/liveRefetch';

type Values = Pick<ContentParameters, 'characterIds' | 'characterAssignments' | 'photoHeroes'>;

export const photoHeroStatesKey = (ids: string[]) => ['characters', 'photo-hero-states', ...ids] as const;

/** Add / rename / remove photo heroes in the wizard values (ids also go to characterIds). */
export function withPhotoHero<T extends Values>(values: T, hero: PhotoHeroDraft): T {
  const heroes = [...(values.photoHeroes ?? []).filter((h) => h.characterId !== hero.characterId), hero];
  const ids = values.characterIds.includes(hero.characterId) ? values.characterIds : [...values.characterIds, hero.characterId];
  return { ...values, photoHeroes: heroes, characterIds: ids };
}

export function withoutPhotoHero<T extends Values>(values: T, characterId: string): T {
  return {
    ...values,
    photoHeroes: (values.photoHeroes ?? []).filter((h) => h.characterId !== characterId),
    characterIds: values.characterIds.filter((id) => id !== characterId),
    characterAssignments: values.characterAssignments.filter((a) => a.characterId !== characterId),
  };
}

export function withPhotoHeroName<T extends Values>(values: T, characterId: string, name: string): T {
  return { ...values, photoHeroes: (values.photoHeroes ?? []).map((h) => (h.characterId === characterId ? { ...h, name } : h)) };
}

/** A photo hero still without a name blocks «Далее» (the name is the one thing we cannot invent). */
export const photoHeroNameMissing = (values: Pick<ContentParameters, 'photoHeroes'>) =>
  (values.photoHeroes ?? []).some((h) => !h.name?.trim());

/** Live identity states of the wizard's photo heroes; polls while any is still drawing. */
export function usePhotoHeroStates(ids: string[]) {
  return useQuery({
    queryKey: photoHeroStatesKey(ids),
    queryFn: () => getPhotoHeroStates(ids),
    enabled: ids.length > 0,
    // character.portrait.* (pushed when the identity lands) invalidates this key; the interval is the fallback
    refetchInterval: (q) => whileWaiting(!!q.state.data?.some((s) => s.state === 'drawing'), 5_000),
  });
}

export interface PhotoHeroUploadProps<T extends Values> {
  values: T;
  onChange: (next: T) => void;
  language?: string;
  onDone?: () => void;
  onCancel?: () => void;
}

/** Consent + photo picker: the pick creates the hero (record + identity job). */
export function PhotoHeroUpload<T extends Values>({ values, onChange, language, onDone, onCancel }: PhotoHeroUploadProps<T>) {
  const t = useTranslations('content.wizard.heroes');
  const queryClient = useQueryClient();
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = (file: File | undefined) => {
    setError(null);
    if (!file || busy) return;
    // fail closed on the client too — no consent, no upload
    if (!consent) { setError(t('fromPhotoConsentRequired')); return; }
    setBusy(true);
    void (async () => {
      try {
        // downscaled first: a phone photograph overruns the Server Action body limit once base64 grows it
        const b64 = await fileToBase64(file);
        const res = await createPhotoHero({ imageBase64: b64, consentVersion: PHOTO_CONSENT_VERSION, language });
        if (!res.success) { setError(res.error); return; }
        onChange(withPhotoHero(values, {
          characterId: res.data.characterId,
          name: res.data.name,
          photoKey: res.data.photoKey,
          previewUrl: res.data.previewUrl,
        }));
        void queryClient.invalidateQueries({ queryKey: characterKeys.all });
        onDone?.();
      } catch {
        setError(t('fromPhotoReadError'));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div data-testid="heroes-from-photo-panel" className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div>
        <p className="text-sm font-medium">{t('fromPhotoTitle')}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('fromPhotoSubtitle')}</p>
      </div>
      <PhotoConsentCheckbox checked={consent} onChange={(v) => { setConsent(v); setError(null); }} />
      <label
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border bg-background p-4 text-center text-xs text-muted-foreground',
          consent ? 'cursor-pointer hover:border-primary/50' : 'cursor-not-allowed opacity-50',
        )}
      >
        {busy ? <Loader2 className="size-5 animate-spin" /> : <Camera className="size-5" />}
        <span>{busy ? t('fromPhotoBusy') : t('fromPhotoPick')}</span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={busy || !consent}
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="heroes-from-photo-file"
        />
      </label>
      {error && <p className="text-xs text-destructive" data-testid="heroes-from-photo-error">{error}</p>}
      {onCancel && (
        <button type="button" onClick={onCancel} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">
          {t('fromPhotoCancel')}
        </button>
      )}
    </div>
  );
}

export interface PhotoHeroCardsProps<T extends Values> {
  values: T;
  onChange: (next: T) => void;
  language?: string;
}

/** The photo heroes of this order: photo, name, identity state (drawing / ready / failed → redraw). */
export function PhotoHeroCards<T extends Values>({ values, onChange, language }: PhotoHeroCardsProps<T>) {
  const t = useTranslations('content.wizard.heroes');
  const heroes = values.photoHeroes ?? [];
  const ids = heroes.map((h) => h.characterId);
  const { data: states = [], refetch } = usePhotoHeroStates(ids);
  const [redrawing, setRedrawing] = useState<string | null>(null);
  if (!heroes.length) return null;

  const commitName = (characterId: string, name: string) => {
    if (name.trim()) void renamePhotoHero(characterId, name);
  };
  const redraw = async (h: PhotoHeroDraft) => {
    setRedrawing(h.characterId);
    try {
      await redrawPhotoHero({ characterId: h.characterId, photoKey: h.photoKey, language });
      await refetch();
    } finally {
      setRedrawing(null);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {heroes.map((h) => {
        const st = states.find((s) => s.characterId === h.characterId);
        const state = st?.state ?? 'drawing';
        return (
          <Card
            key={h.characterId}
            className="relative flex flex-col items-center gap-2 border-primary p-3 ring-2 ring-primary/20"
            data-testid={`heroes-photo-${h.characterId}`}
            data-identity={state}
          >
            <button
              type="button"
              onClick={() => onChange(withoutPhotoHero(values, h.characterId))}
              className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
              aria-label={t('fromPhotoCancel')}
              data-testid={`heroes-photo-remove-${h.characterId}`}
            >
              <X className="size-3" />
            </button>
            <div className="size-14 overflow-hidden rounded-full bg-muted">
              {st?.portraitUrl || h.previewUrl ? (
                // eslint-disable-next-line @next/next/no-img-element -- presigned R2 preview / portrait
                <img src={st?.portraitUrl || h.previewUrl} alt={h.name} className="size-full object-cover" />
              ) : (
                <div className="flex size-full items-center justify-center"><Camera className="size-6 text-muted-foreground" /></div>
              )}
            </div>
            <div className="w-full min-w-0 space-y-1 text-center">
              <input
                value={h.name}
                onChange={(e) => onChange(withPhotoHeroName(values, h.characterId, e.target.value))}
                onBlur={(e) => commitName(h.characterId, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (h.name.trim()) e.currentTarget.blur();
                }}
                placeholder={t('fromPhotoNamePlaceholder')}
                autoFocus={!h.name}
                className={cn('w-full rounded border bg-background px-2 py-1 text-center text-sm', !h.name.trim() && 'border-destructive')}
                data-testid={`heroes-photo-name-${h.characterId}`}
              />
              {!h.name.trim() ? (
                <p className="truncate text-[10px] text-destructive">{t('fromPhotoNameRequired')}</p>
              ) : (
                <p className="flex items-center justify-center gap-1 truncate text-[10px] text-muted-foreground" data-testid={`heroes-photo-state-${h.characterId}`}>
                  {state === 'drawing' && <><Loader2 className="size-3 animate-spin" />{t('identity.drawing')}</>}
                  {state === 'ready' && <><CheckCircle2 className="size-3 text-success" />{t('identity.ready')}</>}
                  {state === 'failed' && <><AlertTriangle className="size-3 text-destructive" />{t('identity.failed')}</>}
                </p>
              )}
              {state === 'failed' && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px]"
                  disabled={redrawing === h.characterId}
                  onClick={() => void redraw(h)}
                  data-testid={`heroes-photo-redraw-${h.characterId}`}
                >
                  <RefreshCw className={cn('size-3', redrawing === h.characterId && 'animate-spin')} />
                  {t('identity.redraw')}
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
